import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { InvoiceMascot, MascotExpression } from '../invoice-tour/invoice-mascot.component';
import './../../scss/components/manage-invoice-tour.style.scss';

const clampGuide = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

/* Step-by-step spotlight tour for the Active Invoices page.
   Built from scratch, no extra dependency. Mounted into document.body via
   React portal so it escapes the violet panel's transform containing block.
   Targets are located by CSS selector — for elements we own we add a
   `data-tour="..."` attribute; for elements inside the table we reuse the
   existing class names. */

type Placement = 'top' | 'bottom' | 'auto';

interface TourStep {
    selector: string | null;          // null = centered welcome / finish (no spotlight)
    titleKey: string;
    bodyKey: string;
    defaultTitle: string;
    defaultBody: string;
    placement?: Placement;
    padding?: number;
}

interface Props {
    open: boolean;
    onClose: () => void;
    /** First name, used in the finish-step "Congratulations {name}!" bubble. */
    userName?: string;
}

/** Invozio logo shown on the celebration mascot's chest badge. */
const INVOZIO_LOGO_SRC = '/assets/svg/invozio-logo.svg';

const PAD = 10;
const TIP_WIDTH = 360;
const TIP_HEIGHT_EST = 260;           // tooltip estimated max height — used for clamping
const VIEWPORT_MARGIN = 12;

const isMobile = () => typeof window !== 'undefined' && window.innerWidth < 768;

const ArrowLeft = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M15 6l-6 6 6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ArrowRight = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const ManageInvoiceTour: React.FC<Props> = ({ open, onClose, userName }) => {
    const { t } = useTranslation();
    const firstName = (userName || '').trim().split(' ')[0];
    const [step, setStep] = useState(0);
    const [rect, setRect] = useState<DOMRect | null>(null);
    // Direction of the last step change. Used as `data-dir` on the tooltip
    // so the inner content (counter / title / body) can slide in from
    // the matching side — right on Next, left on Back.
    const [direction, setDirection] = useState<'forward' | 'back'>('forward');
    const [vp, setVp] = useState<{ w: number; h: number }>({
        w: typeof window !== 'undefined' ? window.innerWidth : 1024,
        h: typeof window !== 'undefined' ? window.innerHeight : 768,
    });

    // Tour-guide mascot (same character as the Create-Invoice tour). Its eyes
    // follow the cursor over the tip; it turns sad when the user hovers Skip.
    const [guideMood, setGuideMood] = useState<MascotExpression>('idle');
    const guideEyesRef = useRef<SVGGElement | null>(null);
    const guideWrapRef = useRef<HTMLSpanElement | null>(null);
    const trackGuideEyes = (e: React.MouseEvent): void => {
        const g = guideWrapRef.current?.getBoundingClientRect();
        if (!g || !guideEyesRef.current) return;
        const ox = g.left + g.width / 2;
        const oy = g.top + g.height * 0.42;
        const dx = clampGuide((e.clientX - ox) / 220, -1, 1);
        const dy = clampGuide((e.clientY - oy) / 220, -1, 1);
        guideEyesRef.current.style.transform = `translate(${dx * 2.6}px, ${dy * 2.2}px)`;
    };
    const resetGuideEyes = (): void => {
        if (guideEyesRef.current) guideEyesRef.current.style.transform = 'translate(0px, 0px)';
    };

    /* ---- Step definitions ----
       All possible steps live here as constants. The actual ordered list
       is built dynamically at tour-open time so that:
       - When there are rows, the tour walks through the 3 action buttons.
       - When the table is empty (no invoices at all), those 3 buttons
         don't exist in the DOM — instead we explain the empty state with
         a dedicated "create your first invoice" step.
       - When the user has a search query / filter active and it returns
         zero matches, we show a different empty-state explanation
         ("no matches — try clearing your filters").
       In both empty cases the 3 action-button steps are skipped (they
       have no targets to highlight anyway). */
    const stepDefs = useMemo(() => ({
        welcome: {
            selector: null,
            titleKey: 'TOUR_WELCOME_TITLE', defaultTitle: 'Welcome to your invoice hub',
            bodyKey: 'TOUR_WELCOME_BODY', defaultBody: 'A quick walkthrough of how this page works. Press Esc anytime to exit, or use the arrow keys to step.',
        },
        kpiTotal: {
            selector: '[data-tour="kpis"] .kpi-card:nth-child(1)',
            titleKey: 'TOUR_KPI_TOTAL_TITLE', defaultTitle: 'Total invoiced',
            bodyKey: 'TOUR_KPI_TOTAL_BODY', defaultBody: 'The total value of every invoice you have ever issued. The progress bar underneath shows what share of that total has actually been collected — the higher the bar, the healthier your cash flow.',
            placement: 'bottom' as Placement,
        },
        kpiPaid: {
            selector: '[data-tour="kpis"] .kpi-card:nth-child(2)',
            titleKey: 'TOUR_KPI_PAID_TITLE', defaultTitle: 'Paid',
            bodyKey: 'TOUR_KPI_PAID_BODY', defaultBody: 'Money your clients have already settled. This number adds up every invoice marked Paid — “cleared and settled”, no further action needed.',
            placement: 'bottom' as Placement,
        },
        kpiOutstanding: {
            selector: '[data-tour="kpis"] .kpi-card:nth-child(3)',
            titleKey: 'TOUR_KPI_OUTSTANDING_TITLE', defaultTitle: 'Outstanding',
            bodyKey: 'TOUR_KPI_OUTSTANDING_BODY', defaultBody: 'Money still owed to you — the total of every invoice that has been sent but not yet paid. Keep an eye on this one; this is what you are waiting on.',
            placement: 'bottom' as Placement,
        },
        tabs: {
            selector: '[data-tour="tabs"]',
            titleKey: 'TOUR_TABS_TITLE', defaultTitle: 'Active vs Credit',
            bodyKey: 'TOUR_TABS_BODY', defaultBody: 'Active Invoices shows every invoice you have sent to clients — paid, unpaid, partly paid and overdue. Credit Invoices shows corrections you have issued against an existing invoice (refunds or adjustments). Your tab is remembered in the URL.',
            placement: 'bottom' as Placement,
        },
        create: {
            selector: '[data-tour="create"]',
            titleKey: 'TOUR_CREATE_TITLE', defaultTitle: 'Create a new invoice',
            bodyKey: 'TOUR_CREATE_BODY', defaultBody: 'Start a fresh invoice from scratch any time.',
            placement: 'bottom' as Placement,
        },
        search: {
            selector: '.invoice-table__search-input',
            titleKey: 'TOUR_SEARCH_TITLE', defaultTitle: 'Find invoices fast',
            bodyKey: 'TOUR_SEARCH_BODY', defaultBody: 'Search by invoice number or client name. Tip: press “/” anywhere on the page to jump straight here.',
            placement: 'bottom' as Placement,
        },
        filters: {
            selector: '.invoice-table__filter-bar',
            titleKey: 'TOUR_FILTERS_TITLE', defaultTitle: 'Narrow it down',
            bodyKey: 'TOUR_FILTERS_BODY', defaultBody: 'Filter by status (Paid / Unpaid / Partly Paid) or stage. The list updates as you change a filter, while the search bar keeps focus.',
            placement: 'bottom' as Placement,
        },
        // ---- Action buttons (only when rows exist) ----
        // Selectors are structural — see comment near buildSteps() for why.
        edit: {
            selector: '.invoice-table-detailed__action-group > .invoice-table-detailed__action-btn:first-child',
            titleKey: 'TOUR_EDIT_TITLE', defaultTitle: 'Edit invoice',
            bodyKey: 'TOUR_EDIT_BODY', defaultBody: 'Opens the invoice so you can change line items, references, or client info. The pencil shows in violet when the invoice can be edited; it dims to grey when editing is locked.',
            placement: 'top' as Placement,
        },
        download: {
            selector: '.invoice-download-btn-wrapper',
            titleKey: 'TOUR_DOWNLOAD_TITLE', defaultTitle: 'Download invoice',
            bodyKey: 'TOUR_DOWNLOAD_BODY', defaultBody: 'Saves the invoice as a PDF on your device. The download icon is always enabled — every invoice can be downloaded, whether it is unpaid, paid, partly paid or part of a credit chain.',
            placement: 'top' as Placement,
        },
        credit: {
            // Targeted by its own class rather than by position. This used to be
            // the adjacent sibling of the download wrapper, but the Send button
            // now renders between the two — so a positional selector here would
            // land on Send instead of Credit.
            selector: '.invoice-table-detailed__action-group > .invoice-credit-btn',
            titleKey: 'TOUR_CREDIT_REQUEST_TITLE', defaultTitle: 'Request credit invoice',
            bodyKey: 'TOUR_CREDIT_REQUEST_BODY', defaultBody: 'Sends a request to the admin asking for this invoice to be cancelled or corrected via a credit invoice. The icon shows in violet when a request can be opened; it dims when a credit invoice already exists, a request has already been sent, or salary has already been processed against this invoice.',
            placement: 'top' as Placement,
        },
        // ---- Empty-state branches (when table has zero rows) ----
        emptyAll: {
            selector: '.mi-empty-state',
            titleKey: 'TOUR_EMPTY_TITLE', defaultTitle: 'Nothing here yet',
            bodyKey: 'TOUR_EMPTY_BODY', defaultBody: 'When you create your first invoice it will show up here as a new row. Each row carries action buttons on the right — Edit, Download and Request credit — but they only appear once at least one invoice exists. Click Create invoice (top-right) to get started.',
            placement: 'top' as Placement,
        },
        emptySearch: {
            selector: '.mi-empty-state',
            titleKey: 'TOUR_NO_MATCH_TITLE', defaultTitle: 'No matching invoices',
            bodyKey: 'TOUR_NO_MATCH_BODY', defaultBody: 'Your current search or filter has no matches, so the table is empty right now. Try a different search term, change the status filter, or clear the filters to see all invoices again. Action buttons (Edit / Download / Request credit) only appear once at least one row is visible.',
            placement: 'top' as Placement,
        },
        // ---- Credit Invoices tab variants ----
        welcomeCredit: {
            selector: null,
            titleKey: 'TOUR_WELCOME_CREDIT_TITLE', defaultTitle: 'Welcome to Credit Invoices',
            bodyKey: 'TOUR_WELCOME_CREDIT_BODY', defaultBody: 'A quick walkthrough of how credit invoices work. These are corrections you have issued against an existing invoice — refunds and adjustments live here. Press Esc anytime to exit, or use the arrow keys to step through.',
        },
        creditTable: {
            selector: '.invoice-table-scroll-wrapper',
            titleKey: 'TOUR_CREDIT_TABLE_TITLE', defaultTitle: 'Your credit invoices',
            bodyKey: 'TOUR_CREDIT_TABLE_BODY', defaultBody: 'Each row is a credit invoice — a correction issued against an existing invoice. You will see the invoice number, the client, the credited amount, the due date and the current status of the credit (pending approval, approved, or deleted). Credit rows do not carry their own action buttons — corrections always start from the matching row on the Active Invoices tab.',
            placement: 'top' as Placement,
        },
        creditEmptyAll: {
            selector: '.mi-empty-state',
            titleKey: 'TOUR_CREDIT_EMPTY_TITLE', defaultTitle: 'No credit invoices yet',
            bodyKey: 'TOUR_CREDIT_EMPTY_BODY', defaultBody: 'Credit invoices show corrections you have issued against existing invoices — refunds or adjustments. They appear here once you have requested a credit. To start one, switch to the Active Invoices tab and click the credit-request icon (the red ✕) on the row you want to correct.',
            placement: 'top' as Placement,
        },
        creditEmptySearch: {
            selector: '.mi-empty-state',
            titleKey: 'TOUR_CREDIT_NO_MATCH_TITLE', defaultTitle: 'No matching credit invoices',
            bodyKey: 'TOUR_CREDIT_NO_MATCH_BODY', defaultBody: 'Your current search has no matches in the credit invoices list. Try a different search term, or clear the search to see all credit invoices again.',
            placement: 'top' as Placement,
        },
        finish: {
            selector: null,
            /* Celebration copy on the last step — matches the spec's
               "Congratulations!" requirement and explicitly tells the
               user where the Start Tour button is so they know how to
               restart it next time. The `data-finish-step` attribute
               (applied to the tooltip below) lets SCSS render a more
               festive look on just this step without touching the
               earlier steps' design. */
            titleKey: 'TOUR_FINISH_TITLE', defaultTitle: 'Congratulations!',
            bodyKey: 'TOUR_FINISH_BODY', defaultBody: "You have completed the Manage Invoice tour. You can restart it anytime using the Start Tour button at the top of the page.",
        },
    }), []);

    /* Inspect the live DOM and decide which list of steps to walk through.
       Read at tour-open time and locked in for the duration of one run.
       Branches on the active tab (URL `?tab=credit` vs default Active) so
       the user always gets a tab-specific tour. */
    const buildSteps = useCallback((): TourStep[] => {
        const tabParam = new URLSearchParams(window.location.search).get('tab');
        const isCredit = tabParam === 'credit';

        // Steps shared by both tabs. The welcome step changes per-tab so the
        // user immediately knows the tour reflects the surface they're on.
        // The Outstanding KPI step was removed because that card no longer
        // exists on the page — its selector
        // `[data-tour="kpis"] .kpi-card:nth-child(3)` would never match and
        // the tour would fall back to a centered tooltip with no spotlight.
        const sharedHead = [
            isCredit ? stepDefs.welcomeCredit : stepDefs.welcome,
            stepDefs.kpiTotal,
            stepDefs.kpiPaid,
            stepDefs.tabs,
            stepDefs.create,
            stepDefs.search,
        ];

        if (isCredit) {
            // ---- Credit Invoices tab ----
            // Credit has no status / stage filter dropdowns and no per-row
            // action buttons, so the filters / Edit / Download / Credit
            // steps are dropped. Instead we show one "what's in here" step
            // when the table has rows, or an empty-state step when not.
            const emptyState = document.querySelector('.mi-empty-state');
            const hasRows = !emptyState;
            if (hasRows) {
                return [...sharedHead, stepDefs.creditTable, stepDefs.finish];
            }
            const searchEl = document.querySelector<HTMLInputElement>('.invoice-table__search-field');
            const hasActiveQuery = (searchEl?.value || '').trim().length > 0;
            return [
                ...sharedHead,
                hasActiveQuery ? stepDefs.creditEmptySearch : stepDefs.creditEmptyAll,
                stepDefs.finish,
            ];
        }

        // ---- Active Invoices tab ----
        const activeBase = [...sharedHead, stepDefs.filters];
        const hasRows = !!document.querySelector('.invoice-table-detailed__action-group');
        if (hasRows) {
            return [...activeBase, stepDefs.edit, stepDefs.download, stepDefs.credit, stepDefs.finish];
        }
        // No rows on Active — pick the right empty-state explanation.
        const searchEl = document.querySelector<HTMLInputElement>('.invoice-table__search-field');
        const filterSelects = Array.from(document.querySelectorAll<HTMLSelectElement>('.invoice-table__filter-select'));
        const hasActiveQuery =
            (searchEl?.value || '').trim().length > 0
            || filterSelects.some(s => (s.value || '') !== '');
        return [...activeBase, hasActiveQuery ? stepDefs.emptySearch : stepDefs.emptyAll, stepDefs.finish];
    }, [stepDefs]);

    /* Step list is rebuilt every time the tour opens so it reflects the
       current table state (loaded / empty / filtered-empty). Initialising
       with an empty array is safe — the component returns null when
       there's no current step. */
    const [steps, setSteps] = useState<TourStep[]>([]);
    useEffect(() => {
        if (open) {
            setSteps(buildSteps());
            setStep(0);
        }
    }, [open, buildSteps]);

    const current = steps[step];
    const lastStep = step === steps.length - 1;

    /* Achievement-style confetti — only meaningful on the finish step,
       but the `useMemo` MUST be called on every render (the early-return
       below depends on `open` / `current`, which would otherwise change
       the number of hooks called from one render to the next and trip
       React's Rules of Hooks: "Rendered more hooks than during the
       previous render"). Memo is keyed on `lastStep` (and `step` so going
       back-and-forth between steps re-rolls a fresh shower each time the
       user lands on the finish step). When `lastStep` is false the memo
       returns an empty array — cheap, no DOM nodes, no animation cost.

       40 pieces is the sweet spot: enough to feel celebratory, few
       enough that low-end devices don't drop frames. Each piece picks:
         - a random x position (0-100% of viewport)
         - a random color from the festive palette
         - a random shape (rectangle "streamer" vs circle "dot")
         - a random staggered delay so they don't fall as a wall
         - a random duration / rotation amount so the fall feels organic
       Math.random() inside React render is fine — the values just need
       to be stable for the lifetime of one finish-step render, which
       the memo guarantees. */
    const confettiPieces = useMemo(() => {
        if (!lastStep) return [];
        const palette = [
            '#7c3aed', '#a855f7', '#c4b5fd', '#5b21b6',  // brand violets
            '#fcd34d', '#f472b6', '#34d399', '#fb923c',  // festive accents
            '#60a5fa', '#ffffff',                         // light blue + white sparkle
        ];
        return Array.from({ length: 40 }, (_, i) => {
            const isStreamer = Math.random() > 0.4;
            return {
                id: i,
                left: Math.random() * 100,                          // % across viewport
                color: palette[Math.floor(Math.random() * palette.length)],
                isStreamer,
                width: isStreamer ? 6 + Math.random() * 4 : 8 + Math.random() * 4,
                height: isStreamer ? 12 + Math.random() * 8 : 0,    // 0 = circle (uses border-radius 50%)
                delay: Math.random() * 1.2,                          // s
                duration: 2.4 + Math.random() * 1.8,                // s
                rotateStart: Math.random() * 360,
                rotateEnd: 360 + Math.random() * 720 * (Math.random() > 0.5 ? 1 : -1),
                sway: 30 + Math.random() * 50,                       // px horizontal sway
            };
        });
    }, [lastStep, step]);

    /* NOTE — we deliberately do NOT lock body scroll while the tour is open.
       Locking it (with `document.body.style.overflow = 'hidden'`) caused two
       problems together: (1) `element.scrollIntoView()` could be blocked, so
       the target never actually came into view; (2) when the tooltip ended
       up below the viewport the user had no way to scroll to reach the
       Next / Skip / Back buttons. Without the lock the user can scroll
       freely, our scroll listener re-measures the target so the spotlight
       stays glued to it, and the tooltip is clamped to the viewport so
       its buttons are always reachable. */

    // Measure the target rect; call after any layout-affecting change.
    const measure = useCallback(() => {
        if (!current?.selector) { setRect(null); return; }
        const el = document.querySelector(current.selector) as HTMLElement | null;
        if (!el) { setRect(null); return; }
        setRect(el.getBoundingClientRect());
    }, [current]);

    /* When the step changes: smooth-scroll the target into view (centered),
       then re-measure. The `out` check skips the scroll if the target is
       already comfortably inside the viewport. */
    useLayoutEffect(() => {
        if (!open) return;
        if (current?.selector) {
            const el = document.querySelector(current.selector) as HTMLElement | null;
            if (el) {
                const r = el.getBoundingClientRect();
                const out = r.top < 80 || r.bottom > window.innerHeight - 80;
                if (out) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
                const id = setTimeout(measure, out ? 420 : 30);
                return () => clearTimeout(id);
            }
        }
        measure();
    }, [open, step, measure, current]);

    /* Keep the spotlight + tooltip glued to the target as the user scrolls
       (manual scroll, or animations that shift the DOM). */
    useEffect(() => {
        if (!open) return;
        const onResize = () => {
            setVp({ w: window.innerWidth, h: window.innerHeight });
            measure();
        };
        window.addEventListener('resize', onResize);
        window.addEventListener('scroll', onResize, true);
        const interval = setInterval(measure, 400);
        return () => {
            window.removeEventListener('resize', onResize);
            window.removeEventListener('scroll', onResize, true);
            clearInterval(interval);
        };
    }, [open, measure]);

    /* Tiny helpers — they both update `direction` *and* the step, so the
       tip's slide-in animation runs from the matching side. */
    const goNext = useCallback(() => {
        setDirection('forward');
        setStep(s => Math.min(s + 1, steps.length - 1));
    }, [steps.length]);
    const goBack = useCallback(() => {
        setDirection('back');
        setStep(s => Math.max(s - 1, 0));
    }, []);

    // Esc closes, arrow keys step
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') onClose();
            else if (e.key === 'ArrowRight') goNext();
            else if (e.key === 'ArrowLeft') goBack();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose, goNext, goBack]);

    // Don't render until both `open` is true AND the step list has been
    // built. `steps` is briefly empty between `open` flipping true and the
    // useEffect that calls `buildSteps()` running — render nothing in that
    // gap to avoid touching `current.selector` etc. on an undefined value.
    if (!open || !current) return null;

    const pad = current?.padding ?? PAD;
    const hasTarget = !!rect;
    const mobile = isMobile();

    // ---- Dim layers (4 quadrants around the spotlight, or one full overlay) ----
    let dims: React.CSSProperties[] = [];
    if (hasTarget) {
        const top = Math.max(0, rect!.top - pad);
        const bottom = Math.min(vp.h, rect!.bottom + pad);
        const left = Math.max(0, rect!.left - pad);
        const right = Math.min(vp.w, rect!.right + pad);
        dims = [
            { top: 0, left: 0, width: vp.w, height: top },
            { top: bottom, left: 0, width: vp.w, height: Math.max(0, vp.h - bottom) },
            { top, left: 0, width: left, height: Math.max(0, bottom - top) },
            { top, left: right, width: Math.max(0, vp.w - right), height: Math.max(0, bottom - top) },
        ];
    } else {
        dims = [{ top: 0, left: 0, width: vp.w, height: vp.h }];
    }

    // ---- Spotlight rectangle ----
    const spotlightStyle: React.CSSProperties = hasTarget ? {
        top: Math.max(0, rect!.top - pad),
        left: Math.max(0, rect!.left - pad),
        width: rect!.width + pad * 2,
        height: rect!.height + pad * 2,
    } : {};

    // ---- Tooltip placement (clamped to viewport so buttons are always reachable) ----
    type TipMode = 'top' | 'bottom' | 'center' | 'sheet';
    let mode: TipMode = 'center';
    let tipStyle: React.CSSProperties = {};

    if (mobile) {
        mode = 'sheet';
    } else if (hasTarget) {
        const r = rect!;
        const spaceBelow = vp.h - r.bottom;
        const spaceAbove = r.top;
        /* SAFE_BUFFER ensures the tooltip never sits flush against the
           target — it has at least 24px of breathing room above its own
           edge. Without this, the Credit step's tooltip would clamp to
           the viewport top and end up visually touching its icon. */
        const SAFE_BUFFER = 24;
        const fitsBelow = spaceBelow >= TIP_HEIGHT_EST + SAFE_BUFFER;
        const fitsAbove = spaceAbove >= TIP_HEIGHT_EST + SAFE_BUFFER;

        // Decide above/below: respect the step's preferred placement when
        // it fits; otherwise use the side that has enough room; finally
        // fall back to whichever has more space (we'll clamp regardless).
        let useBelow: boolean;
        if (current?.placement === 'bottom') useBelow = fitsBelow || !fitsAbove;
        else if (current?.placement === 'top') useBelow = !fitsAbove && fitsBelow;
        else useBelow = fitsBelow || spaceBelow > spaceAbove;

        // Horizontal: center on the target, then clamp into the viewport.
        const leftRaw = r.left + r.width / 2 - TIP_WIDTH / 2;
        const left = Math.min(
            Math.max(VIEWPORT_MARGIN, leftRaw),
            vp.w - TIP_WIDTH - VIEWPORT_MARGIN
        );

        // Vertical: use top-only positioning + clamp so the tooltip's
        // entire content (including the buttons) is always inside the
        // viewport. The `+22` gap (was 14) gives extra breathing room
        // between the spotlight ring and the tooltip card.
        let top: number;
        if (useBelow) top = r.bottom + pad + 22;
        else top = r.top - TIP_HEIGHT_EST - pad - 22;
        top = Math.max(VIEWPORT_MARGIN, Math.min(top, vp.h - TIP_HEIGHT_EST - VIEWPORT_MARGIN));

        /* Arrow horizontal position — pixels from the tooltip's left edge
           to the centre of the target. Clamped 16px inside both edges so
           the arrow never falls off the card's rounded corner. */
        const targetCentre = r.left + r.width / 2;
        const arrowLeftRaw = targetCentre - left;
        const arrowLeft = Math.max(16, Math.min(arrowLeftRaw, TIP_WIDTH - 16));

        mode = useBelow ? 'bottom' : 'top';
        tipStyle = {
            top,
            left,
            width: TIP_WIDTH,
            ['--mi-tour-arrow-left' as any]: `${arrowLeft}px`,
        };
    }

    const title = t(current.titleKey, { defaultValue: current.defaultTitle });
    const body = t(current.bodyKey, { defaultValue: current.defaultBody });

    const overlay = (
        <div className={`mi-tour ${mobile ? 'mi-tour--mobile' : ''}`}>
            {dims.map((s, i) => (
                <div key={i} className="mi-tour__dim" style={s} />
            ))}

            {hasTarget && <div className="mi-tour__spotlight" style={spotlightStyle} />}

            {/* Achievement confetti — rendered only on the celebration
                step. Sits at viewport scale (position: fixed via SCSS)
                so the shower covers the entire page, not just the
                tooltip's box. pointer-events: none in SCSS so it never
                intercepts clicks on the Finish/Back/Skip buttons. */}
            {lastStep && confettiPieces.length > 0 && (
                <div className="mi-tour__confetti" aria-hidden="true">
                    {confettiPieces.map(p => (
                        <span
                            key={p.id}
                            className={`mi-tour__confetti-piece${p.isStreamer ? '' : ' mi-tour__confetti-piece--dot'}`}
                            style={{
                                left: `${p.left}%`,
                                width: `${p.width}px`,
                                height: p.isStreamer ? `${p.height}px` : `${p.width}px`,
                                background: p.color,
                                /* CSS custom properties drive the per-piece
                                   variation inside the shared keyframe — no
                                   need for 40 unique @keyframes blocks. */
                                ['--rs' as any]: `${p.rotateStart}deg`,
                                ['--re' as any]: `${p.rotateEnd}deg`,
                                ['--sway' as any]: `${p.sway}px`,
                                animationDelay: `${p.delay}s`,
                                animationDuration: `${p.duration}s`,
                            }}
                        />
                    ))}
                </div>
            )}

            <div
                className={`mi-tour__tip mi-tour__tip--${mode}`}
                style={mode === 'top' || mode === 'bottom' ? tipStyle : undefined}
                role="dialog"
                aria-modal="true"
                aria-labelledby="mi-tour-title"
                onMouseMove={trackGuideEyes}
                onMouseLeave={resetGuideEyes}
                /* `data-dir` drives the slide-in direction of the inner
                   content (counter / title / body) — `forward` slides from
                   right (Next), `back` slides from left (Back). */
                data-dir={direction}
                /* `data-finish-step` is added only on the last step so
                   SCSS can paint the celebration treatment (sparkle icon,
                   larger title, gradient backdrop) without affecting any
                   of the earlier steps. */
                data-finish-step={lastStep ? 'true' : undefined}
            >
                {/* `key={step}` makes the counter / title / body re-mount on
                    every step change so their CSS entry animations (counter
                    pop, title fade-in + shimmer, body fade-in) fire each
                    time. The outer `mi-tour__tip` div stays mounted, which
                    means its `top` / `left` transitions glide smoothly
                    between adjacent steps instead of snapping. */}
                {/* Guide mascot + step counter — the same character shown on
                    every step (in front of the step count), matching the
                    Create-Invoice tour. Happy on the celebration step. */}
                <div key={`h-${step}`} className="mi-tour__head">
                    {/* Head guide is hidden on the finish step — the large
                        celebration mascot below already carries the character. */}
                    {!lastStep && (
                        <span className="mi-tour__guide" ref={guideWrapRef} aria-hidden="true">
                            <InvoiceMascot expression={guideMood} eyesRef={guideEyesRef} />
                        </span>
                    )}
                    <span className="mi-tour__counter">
                        {t('TOUR_STEP_OF', { defaultValue: '{{current}} of {{total}}', current: step + 1, total: steps.length })}
                    </span>
                </div>

                {/* Celebration medallion — rendered only on the final
                    step. A circular violet-gradient badge hosts a large
                    white star, surrounded by a soft pulsing halo and
                    five twinkling sparkles to read as a "reward unlocked"
                    moment. Hidden from screen readers (decorative —
                    the title + body already convey the message). */}
                {lastStep && (
                    <span key={`f-${step}`} className="mi-tour__finish-icon" aria-hidden="true">
                        {/* Halo behind the badge — pulses continuously
                            (sn-tour-finish-halo keyframe). Separated from
                            the badge so the badge itself can float on a
                            different cadence without doubling animations. */}
                        <span className="mi-tour__finish-halo" />

                        {/* The celebration character — the tour-guide mascot,
                            shown large in place of the old star badge and
                            "dancing" (mi-tour-bot-dance keyframe). Wears the
                            Invozio logo on its chest and cheers by name. */}
                        <span className="mi-tour__finish-bot">
                            <span className="mi-tour__finish-bubble">
                                {firstName
                                    ? t('TOUR_MI_FINISH_CONGRATS_NAMED', { defaultValue: 'Congratulations, {{name}}! 🎉', name: firstName })
                                    : t('TOUR_MI_FINISH_CONGRATS', { defaultValue: 'Congratulations! 🎉' })}
                            </span>
                            <InvoiceMascot expression="happy" logoSrc={INVOZIO_LOGO_SRC} />
                        </span>

                        {/* Five sparkles dotted around the badge. Each
                            uses an index modifier so the SCSS can place
                            them at fixed angles + give each a staggered
                            twinkle delay, producing the "alive" feel
                            without identical-phase blinking. */}
                        {[1, 2, 3, 4, 5].map(i => (
                            <span
                                key={`twinkle-${i}`}
                                className={`mi-tour__finish-twinkle mi-tour__finish-twinkle--${i}`}
                            />
                        ))}
                    </span>
                )}

                <h3 key={`t-${step}`} id="mi-tour-title" className="mi-tour__title">{title}</h3>
                <p key={`b-${step}`} className="mi-tour__body">{body}</p>

                <div className="mi-tour__actions">
                    <button
                        type="button"
                        className="mi-tour__btn mi-tour__btn--skip"
                        onClick={onClose}
                        onMouseEnter={() => setGuideMood('sad')}
                        onMouseLeave={() => setGuideMood('idle')}
                    >
                        {t('TOUR_SKIP', { defaultValue: 'Skip tour' })}
                    </button>
                    <div className="mi-tour__spacer" />
                    <button
                        type="button"
                        className="mi-tour__btn mi-tour__btn--ghost"
                        onClick={goBack}
                        disabled={step === 0}
                        onMouseEnter={() => setGuideMood('confused')}
                        onMouseLeave={() => setGuideMood('idle')}
                    >
                        <ArrowLeft />
                        <span>{t('TOUR_BACK', { defaultValue: 'Back' })}</span>
                    </button>
                    <button
                        type="button"
                        className="mi-tour__btn mi-tour__btn--primary"
                        onClick={() => {
                            if (lastStep) onClose();
                            else goNext();
                        }}
                        onMouseEnter={() => setGuideMood('happy')}
                        onMouseLeave={() => setGuideMood('idle')}
                    >
                        <span>
                            {lastStep
                                ? t('TOUR_FINISH', { defaultValue: 'Finish' })
                                : t('TOUR_NEXT', { defaultValue: 'Next' })}
                        </span>
                        {!lastStep && <ArrowRight />}
                    </button>
                </div>
            </div>
        </div>
    );

    return ReactDOM.createPortal(overlay, document.body);
};

export default ManageInvoiceTour;
